import { Box, Flex, Text } from '@chakra-ui/layout';
import { IconButton, NumberInput, NumberInputField } from '@chakra-ui/react';
import { IconMinusSmall } from 'central-icons/IconMinusSmall';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import { Select as ChakraSelect, type GroupBase } from 'chakra-react-select';
import { isNumber } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from '@revelio/core';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../PageConfigProvider/types/chart';
import {
  type DimensionSource,
  type PlotFilter,
  PlotFilterType,
  type PositionedItem,
} from '../PageConfigProvider/types/item';
import type {
  CompositionMetricValues,
  MetricValues,
} from '../PageConfigProvider/types/metric';
import { SimpleSelectStyles } from './BaseSelectStyles';

type PlotFilterOption = {
  value:
    | PlotFilterType.TOP_N
    | PlotFilterType.BOTTOM_N
    | PlotFilterType.TOP_BOTTOM_N;
  label: string;
};

const plotFilterOptions: PlotFilterOption[] = [
  { value: PlotFilterType.TOP_N, label: 'Top' },
  { value: PlotFilterType.BOTTOM_N, label: 'Bottom' },
  { value: PlotFilterType.TOP_BOTTOM_N, label: 'Top & Bottom' },
];

const TOP_N_EDITOR_MIN = 1;
const TOP_N_EDITOR_MAX = 15;

const TOP_N_DEBOUNCE_MS = 350;

const clampTopBottomLimit = (raw: number): number => {
  if (!isNumber(raw) || Number.isNaN(raw)) return TOP_N_EDITOR_MIN;
  if (raw < TOP_N_EDITOR_MIN) return TOP_N_EDITOR_MIN;
  if (raw > TOP_N_EDITOR_MAX) return TOP_N_EDITOR_MAX;
  return raw;
};

type DebouncedTopBottomNInputProps = {
  filter: PlotFilter;
  plotFilterIndex: number;
  chartItemId: string;
  onCommit: (value: number) => void;
};

const DebouncedTopBottomNInput = ({
  filter,
  plotFilterIndex,
  chartItemId,
  onCommit,
}: DebouncedTopBottomNInputProps) => {
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  });

  const [text, setText] = useState(() =>
    String(clampTopBottomLimit(Number(filter.value)))
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setText(String(clampTopBottomLimit(Number(filter.value))));
  }, [chartItemId, plotFilterIndex, filter.value]);

  const debouncedText = useDebounce(text, TOP_N_DEBOUNCE_MS);

  useEffect(() => {
    const trimmed = debouncedText.trim();
    if (trimmed === '') return;
    const raw = Number(trimmed);
    if (!isNumber(raw) || Number.isNaN(raw)) return;
    const clamped = clampTopBottomLimit(raw);
    if (clamped !== Number(filter.value)) {
      onCommitRef.current(clamped);
    }
  }, [debouncedText, filter.value]);

  return (
    <NumberInput
      size="xs"
      value={text}
      min={TOP_N_EDITOR_MIN}
      max={TOP_N_EDITOR_MAX}
      onChange={(valueString) => setText(valueString)}
      onBlur={() => {
        const trimmed = text.trim();
        if (trimmed === '') return;
        const raw = Number(trimmed);
        if (!isNumber(raw) || Number.isNaN(raw)) return;
        const clamped = clampTopBottomLimit(raw);
        if (clamped !== Number(filter.value)) {
          onCommitRef.current(clamped);
        }
      }}
      clampValueOnBlur={false}
      maxWidth="60px"
    >
      <NumberInputField />
    </NumberInput>
  );
};

export const DataFilterSelector = ({
  item,
  plotFilterMenu = 'all',
}: {
  item: PositionedItem;
  plotFilterMenu?: 'all' | 'top-only';
}) => {
  const { dispatch } = useTabContentConfig();
  const autoAppliedDimensionRef = useRef<DimensionSource | null>(null);
  const metric = item?.plot?.defaultMetricOptions?.[0];
  const { plotFilters } = item.plot ?? {};

  const handleAddFilter = () => {
    dispatch({
      type: 'ADD_PLOT_FILTER',
      payload: {
        id: item.id,
        filter: {
          type: PlotFilterType.TOP_N,
          value: clampTopBottomLimit(10),
        },
      },
    });
  };

  const handleRemoveFilter = (index: number) => {
    dispatch({
      type: 'REMOVE_PLOT_FILTER',
      payload: { plotId: item.id, filterIndex: index },
    });
  };

  const handleChangeFilter = (filter: PlotFilter, index: number) => {
    dispatch({
      type: 'UPDATE_PLOT_FILTER',
      payload: { plotId: item.id, filterIndex: index, filter },
    });
  };

  const topBottomFiltersWithIndices = useMemo(() => {
    if (!plotFilters?.length) return [];

    return plotFilters.reduce<
      Array<{ filter: PlotFilter; plotFilterIndex: number }>
    >((acc, filter, plotFilterIndex) => {
      if (
        filter.type === PlotFilterType.TOP_N ||
        filter.type === PlotFilterType.BOTTOM_N ||
        filter.type === PlotFilterType.TOP_BOTTOM_N
      ) {
        acc.push({ filter, plotFilterIndex });
      }
      return acc;
    }, []);
  }, [plotFilters]);

  useEffect(() => {
    const dimensionSource = item?.plot?.dimensionSource;
    const shouldDefaultTopTen =
      dimensionSource === 'skills' ||
      dimensionSource === 'role' ||
      dimensionSource === 'geography';
    if (!shouldDefaultTopTen) {
      autoAppliedDimensionRef.current = null;
      return;
    }
    if (autoAppliedDimensionRef.current === dimensionSource) return;
    if (topBottomFiltersWithIndices.length > 0) return;

    dispatch({
      type: 'ADD_PLOT_FILTER',
      payload: {
        id: item.id,
        filter: {
          type: PlotFilterType.TOP_N,
          value: clampTopBottomLimit(10),
        },
      },
    });
    autoAppliedDimensionRef.current = dimensionSource;
  }, [
    dispatch,
    item.id,
    item?.plot?.dimensionSource,
    topBottomFiltersWithIndices.length,
  ]);

  useEffect(() => {
    if (plotFilterMenu !== 'top-only') return;
    topBottomFiltersWithIndices.forEach(({ filter, plotFilterIndex }) => {
      if (filter.type === PlotFilterType.TOP_N) return;
      dispatch({
        type: 'UPDATE_PLOT_FILTER',
        payload: {
          plotId: item.id,
          filterIndex: plotFilterIndex,
          filter: {
            type: PlotFilterType.TOP_N,
            value: clampTopBottomLimit(Number(filter.value)),
          },
        },
      });
    });
  }, [dispatch, item.id, plotFilterMenu, topBottomFiltersWithIndices]);

  const sampleSizeFilter = plotFilters?.find(
    (filter) => filter.type === PlotFilterType.TOP_N_SAMPLE_SIZE
  );

  const handleChangeSampleSizeFilter = (value: number) => {
    const filterIndex = plotFilters?.findIndex(
      (filter) => filter.type === PlotFilterType.TOP_N_SAMPLE_SIZE
    );

    if (!isNumber(value) || value < 1) return;

    if (filterIndex === undefined || filterIndex < 0) {
      dispatch({
        type: 'ADD_PLOT_FILTER',
        payload: {
          id: item.id,
          filter: { type: PlotFilterType.TOP_N_SAMPLE_SIZE, value },
        },
      });
      return;
    }
    dispatch({
      type: 'UPDATE_PLOT_FILTER',
      payload: {
        plotId: item.id,
        filterIndex,
        filter: { type: PlotFilterType.TOP_N_SAMPLE_SIZE, value },
      },
    });
  };

  useEffect(() => {
    topBottomFiltersWithIndices.forEach(({ filter, plotFilterIndex }) => {
      const raw = Number(filter.value);
      const clamped = clampTopBottomLimit(raw);
      if (raw !== clamped) {
        dispatch({
          type: 'UPDATE_PLOT_FILTER',
          payload: {
            plotId: item.id,
            filterIndex: plotFilterIndex,
            filter: { ...filter, value: clamped },
          },
        });
      }
    });
  }, [dispatch, item.id, topBottomFiltersWithIndices]);

  const showSampleSizeFilter = getIsSampleSizeFilterEnabled({
    chartType: item?.plot?.chartType,
    dimensionSource: item?.plot?.dimensionSource,
    metric,
  });

  return (
    <Box marginBottom="1.5rem">
      <Flex justifyContent="space-between" marginBottom="0.5rem">
        <Text fontSize="sm" fontWeight={600} marginBottom="0.5rem">
          Filter
        </Text>
        <IconButton
          variant="ghost"
          size="xs"
          aria-label="add-filter"
          colorScheme="blackAlpha"
          isDisabled={
            topBottomFiltersWithIndices &&
            topBottomFiltersWithIndices.length >= 1
          }
          icon={<IconPlusSmall />}
          onClick={handleAddFilter}
        />
      </Flex>
      {topBottomFiltersWithIndices?.map(({ filter, plotFilterIndex }) => (
        <Flex
          key={`${filter.type}-${plotFilterIndex}`}
          justifyContent="space-between"
          alignItems="center"
          marginBottom="1.5rem"
        >
          <Box width="140px">
            {plotFilterMenu === 'top-only' ? (
              <Text fontSize="sm" fontWeight={600} lineHeight="2rem">
                Top
              </Text>
            ) : (
              <ChakraSelect<
                PlotFilterOption,
                false,
                GroupBase<PlotFilterOption>
              >
                chakraStyles={SimpleSelectStyles()}
                options={plotFilterOptions}
                value={{
                  value: filter.type as PlotFilterOption['value'],
                  label:
                    plotFilterOptions.find(
                      (option) => option.value === filter.type
                    )?.label ?? '',
                }}
                onChange={(filterOption) =>
                  filterOption?.value &&
                  handleChangeFilter(
                    {
                      ...filter,
                      type: filterOption?.value,
                      value: clampTopBottomLimit(Number(filter.value)),
                    },
                    plotFilterIndex
                  )
                }
                isSearchable
              />
            )}
          </Box>
          <DebouncedTopBottomNInput
            filter={filter}
            plotFilterIndex={plotFilterIndex}
            chartItemId={item.id}
            onCommit={(value) =>
              handleChangeFilter({ ...filter, value }, plotFilterIndex)
            }
          />
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="delete-filter"
            colorScheme="blackAlpha"
            icon={<IconMinusSmall />}
            onClick={() => handleRemoveFilter(plotFilterIndex)}
          />
        </Flex>
      ))}
      {showSampleSizeFilter && (
        <Flex justifyContent="space-between">
          <Text fontSize="sm" fontWeight={600} lineHeight="2rem">
            Sample Size Filter
          </Text>
          <NumberInput
            size="sm"
            value={sampleSizeFilter?.value}
            onChange={(valueString) => {
              handleChangeSampleSizeFilter(Number(valueString));
            }}
            width="108px"
          >
            <NumberInputField />
          </NumberInput>
        </Flex>
      )}
    </Box>
  );
};

type SupportedMetrics = Extract<
  MetricValues,
  Exclude<CompositionMetricValues, 'headcount' | 'salary'>
>;
const supportedMetrics: Record<SupportedMetrics, boolean> = {
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
};
const isSupportedMetric = (
  metric: MetricValues | undefined
): metric is SupportedMetrics => supportedMetrics[metric as SupportedMetrics];

const getIsSampleSizeFilterEnabled = ({
  chartType,
  dimensionSource,
  metric,
}: {
  chartType?: ChartType;
  dimensionSource?: DimensionSource;
  metric?: MetricValues;
}) => {
  if (chartType !== 'bar') return false;

  if (!dimensionSource || dimensionSource === 'primary') return false;

  return false && isSupportedMetric(metric);
};
